<template>
  <!-- Fields -->
  <section class="shipping-block">
    <!-- Address Autocomplete with Map -->
    <div class="address-autocomplete-input">
      <base-address-autocomplete-input @address-selected="selectAddress" />
    </div>
    <!---->

    <!-- Apartment field -->
    <base-input
      class="col-xs-12 mb10 input shipping-block-field"
      type="text"
      name="apartment-number"
      autocomplete="address-line3"
      :maxlength="28"
      :placeholder="$t('Apartment number')"
      v-model.trim="shipping.apartment"
      @blur="() => $emit('updated', shipping)"
    />
    <!---->

    <!-- Telephone field -->
    <base-telephone-input
      class="create-address shipping-address relative"
      v-if="otherReceiver || showPhone"
      :required="true"
      :validations="phoneValidations"
      :value="shipping.phone_number"
      @update="updatePhoneNumber"
      @input="$v.shipping.phone_number.$touch()"
    />
    <!---->

    <template v-if="otherReceiver">
      <!-- Customer Firstname -->
      <base-input
        class="col-xs-12 mb10 input shipping-block-field"
        type="text"
        name="firstname"
        :maxlength="40"
        :placeholder="$t('First name *')"
        v-model.trim="shipping.firstname"
        @blur="() => $emit('updated', shipping)"
        @input="$v.shipping.firstname.$touch()"
      />
      <!---->

      <!-- Customer Lastname -->
      <base-input
        class="col-xs-12 mb10 input shipping-block-field"
        type="text"
        name="lastname"
        :maxlength="40"
        :placeholder="$t('Last name *')"
        v-model.trim="shipping.lastname"
        @blur="() => $emit('updated', shipping)"
        @input="$v.shipping.lastname.$touch()"
      />
      <!---->
    </template>

    <!-- Comment field -->
    <div class="comment">
      <div class="counter h5">
        {{ commentChars + '/500' }}
      </div>
      <div class="flex textarea-box textarea-wrapper center-xs">
        <textarea
          v-model="shipping.comment" ref="comment"
          @blur="() => $emit('updated', shipping)"
          rows="4"
          class="textarea-custom"
          :placeholder="$t('Comment on the delivery address')"
          maxlength="500"
          data-hj-whitelist
        />
      </div>
    </div>

    <!-- Set default address -->
    <base-checkbox
      v-if="currentUser && !addMainAddress"
      class="col-xs-12 left-pos"
      id="setDefault"
      v-model="shipping.default_shipping"
      @change="() => $emit('updated', shipping)"
    >
      {{ $t('Set main') }}
    </base-checkbox>
  </section>
</template>
<script>
import BaseCheckbox from 'theme/components/core/blocks/Form/BaseCheckbox.vue';
import BaseAddressAutocompleteInput from 'theme/components/core/blocks/Form/BaseAddressAutocompleteInput'
import { required } from 'vuelidate/lib/validators';
import { BaseTelephoneInput as telephoneInputMixin } from '@vue-storefront/core/modules/checkout/components/BaseTelephoneInput';
import { mapState } from 'vuex';
import updateObject from '@vue-storefront/core/scripts/utils/update-object';
import resolveCity from '@vue-storefront/core/scripts/utils/resolve-city';
import checkoutInitialState from '@vue-storefront/core/modules/checkout/store/checkout/initial-state';

const BaseInput = () => import(/* webpackChunkName: "vsf-base-input" */ 'theme/components/core/blocks/Form/BaseInput');

const BaseTelephoneInput = () => import(/* webpackChunkName: "vsf-base-telephone-input" */ 'theme/components/core/blocks/Form/BaseTelephoneInput.vue');

export default {
  name: 'ShippingAddressForm',
  components: {
    BaseInput,
    BaseCheckbox,
    BaseAddressAutocompleteInput,
    BaseTelephoneInput
  },
  mixins: [telephoneInputMixin],
  props: {
    otherReceiver: {
      type: Boolean
    },
    showPhone: {
      type: Boolean,
      default: false
    },
    storedAddress: {
      type: Object,
      required: false,
      default: null
    },
    addMainAddress: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  beforeMount () {
    this.$bus.$on('customer-initialize-shipping-address-form', this.displayAddressForm);
    this.$bus.$on('scroll-to-the-address-form', this.handleScrollToTheForm);
  },
  mounted () {
    if (this.storedAddress) {
      this.shipping = updateObject(this.shipping, {
        ...this.storedAddress,
        default_shipping: !!this.storedAddress.default_shipping
      });
      this.$bus.$emit('address-autocomplete-set-search', this.storedAddress);
    }
  },
  data: () => ({
    shipping: {
      firstname: '',
      lastname: '',
      phone_number: '',
      street: '',
      house: '',
      apartment: '',
      city: '',
      postcode: '',
      state: '',
      country: '',
      default_shipping: false,
      longitude: '',
      latitude: '',
      comment: '',
      api_details: ''
    },
    phoneMeta: {
      isValid: false
    },
    address_id: null
  }),
  validations () {
    let validations = {
      shipping: {
        street: {
          required
        },
        house: {
          required
        }
      }
    }

    if (this.otherReceiver || this.showPhone) {
      validations.shipping.phone_number = {
        required: () => required && 'phoneNumber' in this.phoneMeta,
        isValid: () => this.phoneMeta.isValid
      }
    }

    return validations
  },
  methods: {
    displayAddressForm ({ type, item = null }) {
      if (type === 'add') {
        this.shipping = {...checkoutInitialState.shippingDetails}
      } else if (type === 'edit') {
        this.address_id = item.id
        this.shipping.longitude = item.longitude
        this.shipping.latitude = item.latitude
        this.shipping.api_details = item.api_details
        this.shipping.state = item.state
        this.shipping.country = item.country
        this.shipping.city = item.city
        this.shipping.postcode = (item.postcode === '') ? '' : item.postcode
        this.shipping.street = item.street
        this.shipping.house = item.house
        this.shipping.apartment = item.apartment
        this.shipping.phone_number = item.phone_number
        this.shipping.firstname = item.firstname
        this.shipping.lastname = item.lastname
        this.shipping.comment = item.comment
        this.shipping.default_shipping = !!item.default_shipping
        this.$bus.$emit('address-autocomplete-set-search', this.shipping)
      }
    },
    updatePhoneNumber (value) {
      this.phoneMeta = value;

      if ('formattedNumber' in value) {
        this.shipping.phone_number = value.formattedNumber;
      }
      this.$emit('updated', this.shipping);
    },
    selectAddress (address) {
      if (!address) {
        this.$emit('updated', null);
        return;
      }

      this.shipping.country = address.address.country_code.toUpperCase()
      this.shipping.city = resolveCity(address.address)
      this.shipping.state = address.address.state ? address.address.state : address.address.municipality
      this.shipping.postcode = address.address.postcode
      this.shipping.street = address.address.road
      this.shipping.house = address.address.house_number
      this.shipping.default_shipping = !!address.address.default_shipping
      this.shipping.longitude = address.lon
      this.shipping.latitude = address.lat
      this.shipping.api_details = JSON.stringify(address)
      if (this.currentUser) {
        this.shipping.customer_id = this.currentUser.id
        this.shipping.firstname = this.currentUser.firstname
        this.shipping.lastname = this.currentUser.lastname
        this.shipping.phone_number = this.currentUser.telephone
      }
      this.$v.shipping.$touch()
      this.$v.shipping.street.$touch()
      this.$v.shipping.house.$touch()
      this.$bus.$emit('checkout-after-address-selected');
      this.$bus.$emit('set-has-error-on-address-field', false);
      this.$emit('updated', this.shipping);
    },
    handleScrollToTheForm () {
      this.$el.scrollIntoView({behavior: 'smooth', block: 'center'});
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.user.current
    }),
    phoneValidations () {
      let validation = this.$v.shipping.phone_number
      return [
        {
          condition: validation.$error && !validation.required,
          text: this.$t('Field is required')
        },
        {
          condition: validation.$error && validation.required && !validation.isValid,
          text: this.$t('Incorrect type of number')
        }
      ]
    },
    commentChars () {
      return this.shipping.comment ? this.shipping.comment.length : 0
    }
  },
  beforeDestroy () {
    this.$bus.$off('customer-initialize-shipping-address-form', this.displayAddressForm);
    this.$bus.$off('scroll-to-the-address-form', this.handleScrollToTheForm);
  }
}
</script>
<style lang="scss">
#pickup-checkout {
  .shipping-address {
    width: 100%;

    .vti__dropdown {
      left: 5px;
    }

    .phoneinput input {
      padding-left: 100px;
    }

    .phoneinput label {
      left: 90px;
      top: 12px;
    }
  }
}

.textarea-custom-for-gift {
  max-height: 52px;

  textarea {
    border: none;
    min-height: 55px;
    margin-top: 0;
  }
}

</style>
<style lang="scss" scoped>
.address-autocomplete-input {
  position: relative;
}

::placeholder {
  color: #bdbdbd;
  font-family: "TTNorms Regular", serif;
}

.counter {
  margin-left: 2px;
}

.textarea-custom {
  width: 100%;
  resize: none;
  border: 1px solid #cecdcd;
  border-radius: 10px;
  padding: 10px;
  scrollbar-width: thin;

  &:focus, &:focus-visible {
    outline: none;
    border: 1px solid rgba(17, 187, 46, 0.8);
  }
}

.comment {
  margin: 0 8px 40px;
  position: relative;
  border-radius: 10px;
  overflow: hidden;

  .counter {
    position: absolute;
    right: 16px;
    bottom: 1px;
    background: white;
    padding: 0 4px 2px;
    border-radius: 4px;
  }
}
.left-pos {
  text-align: left;
}
</style>
